const nextFrame = () => {
    return new Promise(resolve => {
        requestAnimationFrame(() => {
            requestAnimationFrame(resolve);
        });
    });
};

const afterTransition = element => {
    return new Promise(resolve => {
        const firstDuration = getComputedStyle(element)
            .transitionDuration
            .split(",")[0]
            .trim();
        const value = Number.parseFloat(firstDuration);
        const duration = firstDuration.endsWith("ms") ? value : value * 1000;
        setTimeout(resolve, duration);
    });
};

const selectInGroup = (buttons, picked) => {
    buttons.forEach(button => {
        const active = button === picked;
        button.classList.toggle("selected", active);
        button.setAttribute("aria-pressed", active ? "true" : "false");
    });
};

const pickedName = buttons => {
    const picked = buttons.find(button => button.classList.contains("selected"));
    const label = picked?.querySelector("p");
    return label === null || label === undefined ? "" : label.textContent.trim();
};

// Button labels ("New York", "Neon Green") map to D0 render file slugs
// ("new-york", "neon-green").
const slug = text => text.toLowerCase().replace(/\s+/g, "-");

export const initPlayground = async () => {
    // Two phones below xl share the wallpaper via class (lock + home on xl).
    const wallpapers = [...document.querySelectorAll(".playground-wallpaper")];
    const combo = document.querySelector("#playground-combo");
    const lockOverlay = document.querySelector("#playground-lock");
    const homeOverlay = document.querySelector("#playground-home");
    const locationPicks = [...document.querySelectorAll(".playground-location")];
    const stylePicks = [...document.querySelectorAll(".playground-style")];
    const screenButtons = [...document.querySelectorAll(".playground-screen-button")];
    if (wallpapers.length === 0 || locationPicks.length === 0 || stylePicks.length === 0) {
        return;
    }

    // Slice 2 fix: combo URLs come from the hidden preload block in HTML, where
    // Parcel has rewritten them to the hashed dist paths — JS must never
    // construct asset paths itself. (The block also preloads all 16 natively.)
    const comboSrc = {};
    document.querySelectorAll(".playground-preload img").forEach(img => {
        comboSrc[img.dataset.combo] = img.src;
    });

    const renderCombo = async () => {
        const key = `${slug(pickedName(locationPicks))}-${slug(pickedName(stylePicks))}`;
        const target = comboSrc[key];
        if (combo !== null) {
            combo.textContent = `${pickedName(locationPicks)} + ${pickedName(stylePicks)}`;
        }
        if (target === undefined) {
            return;
        }
        await Promise.all(wallpapers.map(async wallpaper => {
            if (wallpaper.src === target) {
                return;
            }
            wallpaper.classList.add("is-fading");
            await afterTransition(wallpaper);
            wallpaper.src = target;
            await nextFrame();
            wallpaper.classList.remove("is-fading");
        }));
    };

    locationPicks.forEach(pick => {
        pick.addEventListener("click", () => {
            selectInGroup(locationPicks, pick);
            renderCombo();
        });
    });
    stylePicks.forEach(pick => {
        pick.addEventListener("click", () => {
            selectInGroup(stylePicks, pick);
            renderCombo();
        });
    });

    screenButtons.forEach(button => {
        button.addEventListener("click", () => {
            selectInGroup(screenButtons, button);
            const showLock = button.dataset.screen !== "home";
            if (lockOverlay !== null) {
                lockOverlay.hidden = !showLock;
            }
            if (homeOverlay !== null) {
                homeOverlay.hidden = showLock;
            }
        });
    });

    // On xl both phones are visible side by side (toggle is hidden), so force
    // the lock phone back to its lock state — a mobile toggle to home must not
    // leak into the dual-phone view on resize. Breakpoint read from the theme
    // so JS tracks the xl: utilities in CSS instead of duplicating the value.
    const xl = getComputedStyle(document.documentElement)
        .getPropertyValue("--breakpoint-xl").trim() || "1280px";
    const wide = window.matchMedia(`(min-width: ${xl})`);
    const syncDualPhones = () => {
        if (!wide.matches) {
            return;
        }
        document.querySelector('.playground-screen-button[data-screen="lock"]')?.click();
    };
    wide.addEventListener("change", syncDualPhones);
    syncDualPhones();
};
